import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { HomeController } from '@shared/src/controllers/home/home.controller';
import { HString } from '@shared/src/datatypes/HString';
import { HTags } from '@shared/src/datatypes/HTags';
import { ActionDto } from '@shared/src/dtos/maintenance/action/actionDto';
import { ActionsStripDto } from '@shared/src/dtos/maintenance/action/ActionsStripDto';
import { ShipmentDeliveryLineBatchInfoDetailDto, ShipmentDeliveryLineBatchInfoDto } from '@shared/src/dtos/shipment/delivery/ShipmentDeliveryLineBatchInfoDto';
import { PalletTypes } from '@shared/src/enums/PalletTypes';
import { DeliveryLineColumns } from './DeliveryLineColumns';
import { IDeliveryLine } from './IDeliveryLine';

/***
 * Una fila de la taula de línies d'albarà, compartida pels dos editors
 * (albarà del shipment i línies del CMR). Quins camps es pinten no ho decideix
 * el component: ho diu el DeliveryLineColumns que rep, que al seu torn depèn
 * del mode (edició/visualització) i de si les línies tenen dades.
 *
 * No desa res ni manté estat propi: tot el que passa s'emet cap amunt, al
 * SharedDeliveryLinesBaseComponent que el conté.
 */
@Component({
    selector: 'shared-delivery-line',
    templateUrl: './shared.delivery.line.component.html',
    styleUrls: ['./shared.delivery.line.component.scss']
})
export class SharedDeliveryLineComponent {

    @Input() public line: IDeliveryLine;
    @Input() public columns: DeliveryLineColumns = new DeliveryLineColumns();
    /*** Camps que suporta l'editor, sense filtrar per si hi ha dada (veure detailProperties) */
    @Input() public allColumns: DeliveryLineColumns = new DeliveryLineColumns();
    @Input() public readonly: boolean = false;
    @Input() public expanded: boolean = false;
    @Input() public showDelete: boolean = true;
    /*** Restringeix els paquets seleccionables als del shipment (p.e. ["S:123"]) */
    @Input() public packageFixedFilterKeys: Array<string> = [];

    @Output() public valueChanged: EventEmitter<IDeliveryLine> = new EventEmitter();
    @Output() public expandToggled: EventEmitter<IDeliveryLine> = new EventEmitter();
    @Output() public deleteRequested: EventEmitter<IDeliveryLine> = new EventEmitter();
    @Output() public deleteConfirmed: EventEmitter<IDeliveryLine> = new EventEmitter();
    @Output() public deleteCancelled: EventEmitter<void> = new EventEmitter();
    @Output() public addBatch: EventEmitter<IDeliveryLine> = new EventEmitter();
    @Output() public deleteBatch: EventEmitter<ShipmentDeliveryLineBatchInfoDetailDto> = new EventEmitter();
    @Output() public batchChanged: EventEmitter<IDeliveryLine> = new EventEmitter();

    public enumPalletTypes = PalletTypes;
    public useIonic: boolean = false;

    /*** Accions de la línia (esborrar). Van per l'strip d'actions, no com a botons solts. */
    public lineActions: ActionsStripDto = new ActionsStripDto();

    constructor(@Inject("HomeController") protected homeController: HomeController,
        protected translateService: TranslateService) {
        if (homeController.useIonic)
            this.useIonic = homeController.useIonic();
        this.buildLineActions();
    }

    private buildLineActions() {
        let actions = new ActionsStripDto();
        // showAs=0 -> icona+text (veure ActionDto.getActionShowAs*: qualsevol altre valor no en pinta cap dels dos)
        actions.add(ActionDto.build(SharedDeliveryLineComponent.ACTIONDELETE, 'DELETE.BUTTON',
            'DELETE.BUTTON', 'fa-trash', 90, 0, 1, '').danger());
        this.lineActions = actions;
    }

    public static readonly ACTIONDELETE: string = "DELIVERYLINE.DELETE";

    public onLineAction(action: ActionDto) {
        if (action == null || this.readonly)
            return;
        if (action.actionId === SharedDeliveryLineComponent.ACTIONDELETE)
            this.deleteRequested.next(this.line);
    }

    public onValueChanged() {
        if (this.readonly)
            return;
        this.valueChanged.next(this.line);
    }

    public onExpand() {
        this.expandToggled.next(this.line);
    }

    public onDeleteRequested() {
        this.deleteRequested.next(this.line);
    }

    public onDeleteConfirmed() {
        this.deleteConfirmed.next(this.line);
    }

    public onDeleteCancelled() {
        this.deleteCancelled.next();
    }

    public onAddBatch() {
        this.addBatch.next(this.line);
    }

    public onDeleteBatch(detail: ShipmentDeliveryLineBatchInfoDetailDto) {
        this.deleteBatch.next(detail);
    }

    public onBatchChanged() {
        this.batchChanged.next(this.line);
    }

    public getResourcePalletType(type: PalletTypes): string {
        if (type == null || type == PalletTypes.None)
            return "";
        return this.translateService.instant("PALLETTYPES." + type.toUpperCase());
    }

    /*** Camps que viuen a la zona desplegable (la primera línia va a part) */
    private static readonly DETAILFIELDS: string[] = ['netKg', 'boxes', 'bags', 'sacks', 'litres', 'wDimension',
        'yDimension', 'hDimension', 'm3', 'palletCount', 'palletType', 'packageId', 'userTags', 'publicTags',
        'privateTags', 'batchInfo'];

    private static readonly DETAILCAPTIONS: { [field: string]: string } = {
        netKg: 'DELIVERYLINE.NETKG.CAPTION',
        boxes: 'DELIVERYLINE.BOXES.CAPTION',
        bags: 'DELIVERYLINE.BAGS.CAPTION',
        sacks: 'DELIVERYLINE.SACKS.CAPTION',
        litres: 'DELIVERYLINE.LITRES.CAPTION',
        wDimension: 'DELIVERYLINE.WDIMENSION.CAPTION',
        yDimension: 'DELIVERYLINE.YDIMENSION.CAPTION',
        hDimension: 'DELIVERYLINE.HDIMENSION.CAPTION',
        m3: 'DELIVERYLINE.M3.CAPTION',
        palletCount: 'DELIVERYLINE.PALLETCOUNT.CAPTION',
        palletType: 'DELIVERYLINE.PALLETTYPE.CAPTION',
        packageId: 'DELIVERYLINE.PACKAGE.CAPTION',
        userTags: 'DELIVERYLINE.USERTAGS.CAPTION',
        publicTags: 'DELIVERYLINE.PUBLICTAGS.CAPTION',
        privateTags: 'DELIVERYLINE.PRIVATETAGS.CAPTION'
    };

    /*** Text que es mostra quan una dada no té valor */
    public static readonly EMPTYVALUE: string = "---";

    /***
     * El detall en visualització es pinta com a "prompt: valor", no com a
     * camps. Es llisten TOTES les propietats que aquest editor suporta (menys
     * els lots, que tenen el seu propi bloc), i les que no tenen dada surten
     * amb "---" en comptes de en blanc o "null".
     */
    public get detailProperties(): Array<{ caption: string, value: string }> {
        let result: Array<{ caption: string, value: string }> = [];
        if (this.allColumns == null || this.line == null)
            return result;

        for (let field of SharedDeliveryLineComponent.DETAILFIELDS) {
            if (field === 'batchInfo' || !this.allColumns[field])
                continue;
            result.push({
                caption: SharedDeliveryLineComponent.DETAILCAPTIONS[field],
                value: this.detailValue(field)
            });
        }
        return result;
    }

    private detailValue(field: string): string {
        let empty = SharedDeliveryLineComponent.EMPTYVALUE;

        if (field === 'palletType') {
            let caption = this.getResourcePalletType(this.line.palletType);
            return HString.isNullOrNullString(caption) ? empty : caption;
        }

        if (field === 'userTags' || field === 'publicTags' || field === 'privateTags') {
            let tags = HTags.getTags(this.line[field]);
            return tags.length > 0 ? tags.join(", ") : empty;
        }

        let value = this.line[field];
        if (value == null || value === "" || +value === 0)
            return empty;
        return value.toString();
    }

    /***
     * Un bloc del desplegable s'ha de pintar? Només depèn de si l'editor
     * suporta aquest camp (allColumns), no de si AQUESTA línia hi té valor:
     * el chevron i el seu contingut es mostren sempre, i els camps buits
     * surten com "---" (veure detailValue).
     */
    public showDetail(field: string): boolean {
        return this.allColumns != null && !!this.allColumns[field];
    }

    /***
     * El chevron es mostra sempre que l'editor suporti alguna propietat de
     * detall, independentment de si la línia concreta hi té dades.
     */
    public get hasExpandableContent(): boolean {
        return SharedDeliveryLineComponent.DETAILFIELDS.some(f => this.showDetail(f));
    }
}
